import apiClient from './api';
import type { CreateSubjectSchema, UpdateSubjectSchema, BulkCreateSubjectsSchema } from '@/types/subject_schema';

export default {
  /**
   * Get all subjects with optional filters
   * @param page - Page number (default: 1)
   * @param pageSize - Number of items per page (default: 20)
   * @param category - Filter by category (optional)
   * @param isActive - Filter by active status (optional)
   * @param search - Search by name or code (optional)
   */
  async getAllSubjects(
    page: number = 1,
    pageSize: number = 20,
    category?: string,
    isActive?: boolean,
    search?: string
  ) {
    const params: Record<string, any> = { page, offset: pageSize };

    if (category) params.category = category;
    if (isActive !== undefined) params.is_active = isActive;
    if (search) params.search = search;

    return await apiClient.get('/subjects', { params });
  },

  /**
   * Get subject by ID
   * @param id - Subject ID
   */
  async getSubjectById(id: number) {
    return await apiClient.get(`/subjects/${id}`);
  },

  /**
   * Get all subject categories
   */
  async getSubjectCategories() {
    return await apiClient.get('/subjects/categories');
  },

  /**
   * Create a new subject
   * @param subject - Subject data
   */
  async createSubject(subject: CreateSubjectSchema) {
    return await apiClient.post('/subjects', subject);
  },

  /**
   * Bulk create subjects
   * @param data - Bulk subjects data
   */
  async bulkCreateSubjects(data: BulkCreateSubjectsSchema) {
    return await apiClient.post('/subjects/bulk', data);
  },

  /**
   * Update subject
   * @param id - Subject ID
   * @param subject - Subject data to update
   */
  async updateSubject(id: number, subject: UpdateSubjectSchema) {
    return await apiClient.put(`/subjects/${id}`, subject);
  },

  /**
   * Delete subject
   * @param id - Subject ID
   */
  async deleteSubject(id: number) {
    return await apiClient.delete(`/subjects/${id}`);
  },

  /**
   * Get active subjects only
   * @param page - Page number
   * @param pageSize - Number of items per page
   */
  async getActiveSubjects(page: number = 1, pageSize: number = 100) {
    return await this.getAllSubjects(page, pageSize, undefined, true);
  },

  /**
   * Get subjects by category
   * @param category - Subject category
   * @param page - Page number
   * @param pageSize - Number of items per page
   */
  async getSubjectsByCategory(category: string, page: number = 1, pageSize: number = 20) {
    return await this.getAllSubjects(page, pageSize, category);
  },

  /**
   * Search subjects
   * @param searchTerm - Search term
   * @param page - Page number
   * @param pageSize - Number of items per page
   */
  async searchSubjects(searchTerm: string, page: number = 1, pageSize: number = 20) {
    return await this.getAllSubjects(page, pageSize, undefined, undefined, searchTerm);
  },
};
